import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ShieldCheck } from 'lucide-react';
import { api, errorMessage } from '../../lib/api.js';
import { useAuthStore } from '../../lib/auth-store.js';
import { Button, Field, Input } from '../../components/ui/index.js';

export function Login() {
  const nav = useNavigate();
  const signIn = useAuthStore((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthFrame title="Sign in" subtitle="Admin console — authorised personnel only">
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const t = await api.admin.login({ body: { email, password } });
            signIn({
              accessToken: t.accessToken,
              role: t.role,
              roleName: t.roleName,
              permissions: t.permissions,
              mfaVerified: t.mfaVerified,
              email,
            });
            nav('/mfa', { replace: true });
          } catch (x) {
            setErr(errorMessage(x));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Email" required>
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Password" required>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {err && (
          <p className="rounded-md bg-red-600/10 px-3 py-2 text-sm text-red-600" role="alert">
            {err}
          </p>
        )}
        <Button type="submit" className="w-full" loading={busy}>
          Continue
        </Button>
        <p className="text-center text-xs text-grey-500">
          Multi-factor authentication is required on the next step.
        </p>
      </form>
    </AuthFrame>
  );
}

export function AuthFrame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 p-6">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-navy-900" />
          <div>
            <h1 className="text-lg font-semibold text-grey-900">{title}</h1>
            <p className="text-xs text-grey-500">{subtitle}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
